'use client';

import {
  Carousel,
  CarouselContent,
  type CarouselApi,
  CarouselPrevious,
  CarouselNext,
  CarouselItem,
} from './carousel';
import { useEffect, useMemo, useState } from 'react';

type CarouselWrapperProps = {
  itemsCount: {
    mobile: number;
    tablet: number;
    desktop: number;
    total: number;
  };
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  options?: {
    hiddeArrow?: boolean;
    autoplay?: boolean;
    autoplayDelay?: number;
  }
};

export function CarouselWrapper({
  className,
  itemsCount,
  children,
  contentClassName,
  options,
}: CarouselWrapperProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [slidesInView, setSlidesInView] = useState<number[]>([]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateSlidesInView = () => {
      setSlidesInView(api.slidesInView());
    };

    api.on('settle', updateSlidesInView);

    updateSlidesInView();

    return () => {
      api.off('settle', updateSlidesInView);
    };
  }, [api]);

  function goToSlide(index: number) {
    if (!api) {
      return;
    }
    api.scrollTo(index);
  }

  const arrowClassName = useMemo(() => {
    if (options?.hiddeArrow) {
      return 'hidden';
    }

    const classNames = ['rounded-full'];
    if (itemsCount.total <= itemsCount.mobile) {
      classNames.push('hidden');
    }
    classNames.push(itemsCount.total <= itemsCount.tablet ? 'md:hidden' : 'md:flex');
    classNames.push(itemsCount.total <= itemsCount.desktop ? 'lg:hidden' : 'lg:flex');

    return classNames.join(' ');
  }, [itemsCount, options]);

  return (
    <div className={className}>
      <Carousel
        opts={{ align: 'start', loop: true }}
        setApi={setApi}
        className="w-full"
        autoplay={options?.autoplay}
        duration={options?.autoplayDelay}
      >
        <CarouselContent className={contentClassName}>{children}</CarouselContent>
        <CarouselPrevious className={arrowClassName} />
        <CarouselNext className={arrowClassName} />
      </Carousel>
    </div>
  );
}

export { CarouselItem };

export default CarouselWrapper;
